import { useCallback } from "react";
import { toIsoStringShort } from "../../utils";
import { FileInfo, useApi } from "../api";
import { Worklog } from "./types";

// Global cache: shared across all uses of useJiraApi
const worklogsCache = new Map<string, Promise<Worklog[]>>();

export const useJiraApi = () => {
  const api = useApi();

  const useWorklogs = useCallback(
    (from: string, to: string, skipCache?: boolean) => {
      const url = `/jira/worklogs?from=${formatToDate(from)}&to=${formatToDate(to)}`;
      const cacheKey = url;
      const cached = worklogsCache.get(cacheKey);

      if (!skipCache && cached) {
        return cached;
      }

      const request = api.get<Worklog[]>({ url });
      worklogsCache.set(cacheKey, request);

      return request;
    },
    [api]
  );

  const invalidateWorklogsCache = useCallback(() => {
    worklogsCache.clear();
  }, []);

  const exportExcel = useCallback(
    (from: string, to: string) => {
      const url = `/jira/worklogs?from=${formatToDate(from)}&to=${formatToDate(to)}`;
      const headers = new Headers();
      headers.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, headers });
      return response;
    },
    [api]
  );

  return { useWorklogs, exportExcel, invalidateWorklogsCache };
};

// en-CA because Canada follows the YYYY-MM-DD format by default
const formatToDate = (date?: string) => (date ? toIsoStringShort(new Date(date)) : "");
